/* eslint @typescript-eslint/no-var-requires: "off" */

import React, { useEffect, useState } from 'react';
import FileUploader from './FileUploader';
import './ImageUploader.scss';
import ModalDialog from '../ModalDialog';
import { FilePondFile } from 'filepond';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import { FileUploadApi } from '../../../api/FileUploadApi';
import { ApiFile, UploadedImage } from '../CommonTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth0 } from '@auth0/auth0-react';
import { t } from 'i18next';

export type ImageUploaderProps = {
    name: string;
    title: string;
    UploadedImage?: UploadedImage;
    allowPdf?: boolean;
    saveOnUpload?: boolean;
    imagePreview?: boolean;
    onImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    amendPersonalDetails?: boolean;
    onUploadComplete?: () => void;
};

type SaveResponse = {
    success: boolean;
    imageId: string;
    errorMessage: string;
};

// TODO: must be renamed and refactored (imgs and pdf are supported)
const ImageUploader = ({
    name,
    title,
    UploadedImage,
    allowPdf,
    saveOnUpload = true,
    imagePreview = true,
    onImageChange
}: ImageUploaderProps): JSX.Element => {
    const [showModal, setShowModal] = useState(false);
    const [disableSave, setDisableSave] = useState(true);
    const [file, setFile] = useState<FilePondFile>(undefined);
    const [imageUploaded, setImageUploaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveErrorMessage, setSaveErrorMessage] = useState<string>(undefined);
    const { getAccessTokenSilently } = useAuth0();

    const UploadControlLabels = {
        ImageOrPdf: t('image_uploader_pdf_title'),
        Default: t('image_uploader_photo_title')
    };

    const previewAvailable = (): boolean => {
        if (
            UploadedImage &&
            (UploadedImage.uploadedPath.toLowerCase().endsWith('.heic') ||
                UploadedImage.uploadedPath.toLowerCase().endsWith('.heif') ||
                UploadedImage.uploadedPath.toLowerCase().endsWith('.pdf'))
        )
            return false;
        else return true;
    };
    const onPreview = (event: React.FormEvent) => {
        event.stopPropagation();
    };
    const onCancel = (): void => setShowModal(false);

    useEffect(() => {
        if (UploadedImage && UploadedImage.image !== undefined) {
            setImageUploaded(true);
        }
    }, []);

    const saveFileSuccess = (response: SaveResponse) => {
        if (response.success === false) {
            console.log(`Save file error: ${response.errorMessage}`);
        } else {
            console.log(`Image saved: ${response.imageId}`);

            setImageUploaded(true);
            setDisableSave(true);
            setShowModal(false);
            onImageChange(
                UploadedImage?.type,
                file?.serverId || '',
                response.imageId,
                file?.file ? URL.createObjectURL(file?.file) : '',
                file?.filename
            );
            setFile(undefined);
        }

        setSaving(false);
    };

    const saveFileFailure = () => {
        setSaving(false);
        setSaveErrorMessage(t('image_save_error'));
    };

    const onSave = (): void => {
        const saveFiles = async () => {
            const apiFile: ApiFile = {
                filename: file.filename,
                filepath: file.serverId,
                filetype: file.fileType
            };

            const token = await getAccessTokenSilently();

            new FileUploadApi().saveFiles(
                apiFile,
                token,
                saveFileSuccess,
                saveFileFailure
            );
        };

        if (file !== undefined) {
            setSaving(true);
            setSaveErrorMessage(undefined);
            saveFiles();
        }
    };

    const onConfirmClick = (): void => {
        if (file !== undefined) {
            console.log(file);
            setSaving(true);
            setSaveErrorMessage(undefined);

            setImageUploaded(true);
            setDisableSave(true);
            setShowModal(false);
            onImageChange(
                file.fileType,
                file?.serverId || '',
                file.id,
                file?.file ? URL.createObjectURL(file?.file) : '',
                file?.filename
            );
            setFile(undefined);
        }
    };

    const onUploadImageClick = (): void => {
        setShowModal(true);
        setSaving(false);
        setSaveErrorMessage(undefined);
    };

    useEffect(() => {
        setDisableSave(!file);
    }, [file]);

    const onFileChange = (files: FilePondFile[]) => {
        const uploadedFile = files && files.length > 0 ? files[0] : undefined;

        setFile(uploadedFile);
    };

    const renderImageComponent = () => {
        if (!imageUploaded) {
            return (
                <div className='imageStyle-pre-upload' id={name}>
                    <p>
                        {allowPdf
                            ? UploadControlLabels.ImageOrPdf
                            : UploadControlLabels.Default}
                    </p>
                    <FontAwesomeIcon icon={faCamera} />
                </div>
            );
        } else {
            if (previewAvailable()) {
                return (
                    <img
                        className='imageStyle'
                        id={name}
                        src={UploadedImage?.image}
                    />
                );
            } else {
                return (
                    <div className='imageStyle-pre-upload' id={name}>
                        <p className='small'>
                            {t('upload_successful')}
                            <br />
                            <a
                                href={UploadedImage?.image}
                                download={UploadedImage?.filename}
                                onClick={onPreview}>
                                {UploadedImage?.filename}
                            </a>
                        </p>
                    </div>
                );
            }
        }
    };

    const renderModal = () => {
        return (
            <ModalDialog
                showDialog={showModal}
                title={title}
                primaryButton={
                    saveOnUpload
                        ? {
                              text: t('save'),
                              disabled: disableSave,
                              waitValue: saving,
                              waitText: t('saving'),
                              onClick: onSave
                          }
                        : {
                              text: t('confirm'),
                              disabled: disableSave,
                              waitValue: saving,
                              waitText: t('saving'),
                              onClick: onConfirmClick
                          }
                }
                secondaryButton={{
                    text: t('cancel'),
                    onClick: onCancel
                }}
                errorMessage={saveErrorMessage}
                onCloseClick={onCancel}>
                <FileUploader
                    allowMultiple={false}
                    allowImagePreview={true}
                    onFileChange={onFileChange}
                    allowPdfs={allowPdf}
                />
            </ModalDialog>
        );
    };

    const imageComponent = renderImageComponent();
    const modalComponent = renderModal();

    if (!imagePreview) {
        return (
            <div
                id={name}
                data-testid='claim-image-uploader'
                aria-label={name}
                className='card-header header-right claim'
                onClick={onUploadImageClick}>
                Upload
                {modalComponent}
            </div>
        );
    }

    return (
        <div className='highlighter' onClick={onUploadImageClick}>
            {imageComponent}
            {modalComponent}
        </div>
    );
};

export default ImageUploader;
